import React, { useState, useRef, useEffect } from "react";
import { AlertCircle, GripVertical, Link2, Loader2, RefreshCw, Trash2 } from "lucide-react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { useIsDesktop } from "@/hooks/useMediaQuery";

interface ExchangeTileProps {
  code: string;
  name: string;
  isConnected: boolean;
  freeBalance?: number;
  usedBalance?: number;
  errorMessage?: string;
  isBalancePending?: boolean;
  onConnect?: () => void;
  onRemove?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  compact?: boolean;
  showDragHandle?: boolean;
  onDragHandlePointerDown?: (event: React.PointerEvent) => void;
  /** Shown instead of balances (Upbit has no trading balance). */
  statusText?: string;
}

/** What each venue is used for in Trigger (futures only; Upbit is a signal source). */
const EXCHANGE_SUBTITLES: Record<string, string> = {
  binance: "USDⓈ-M Futures trading",
  bybit: "USDT Perpetual trading",
  gateio: "USDT Futures trading",
  kucoin: "USDT Futures trading",
  aster: "Perpetual trading",
  hyperliquid: "Perpetual trading",
  upbit: "Faster listing alerts, no trading",
};

const exchangeIcons = {
  binance: "/exchanges-logo/binance.webp",
  "binance-demo": "/exchanges-logo/binance.webp",
  bybit: "/exchanges-logo/bybit.webp",
  "bybit-demo": "/exchanges-logo/bybit.webp",
  gateio: "/exchanges-logo/gateio.webp",
  "gateio-demo": "/exchanges-logo/gateio.webp",
  kucoin: "/exchanges-logo/kucoin.webp",
  aster: "/exchanges-logo/aster.webp",
  hyperliquid: "/exchanges-logo/hyperliquid.webp",
  upbit: "/exchanges-logo/upbit.svg",
};

export const ExchangeTile: React.FC<ExchangeTileProps> = ({
  code,
  name,
  isConnected,
  freeBalance,
  usedBalance,
  errorMessage,
  isBalancePending = false,
  onConnect,
  onRemove,
  onRefresh,
  isRefreshing = false,
  compact = false,
  showDragHandle = false,
  onDragHandlePointerDown,
  statusText,
}) => {
  const isDesktop = useIsDesktop();
  const isCompactLayout = compact;
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isClickExpanded, setIsClickExpanded] = useState(false);
  const tileRef = useRef<HTMLDivElement>(null);
  const trashContainerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const currentXRef = useRef<number>(0);
  const swipeOffsetRef = useRef<number>(0);

  // Keep ref in sync with state
  useEffect(() => {
    swipeOffsetRef.current = swipeOffset;
  }, [swipeOffset]);

  // Defensive check for undefined values
  const displayName = name || "Unknown Exchange";

  // Format balances for display
  const formattedFreeBalance =
    freeBalance !== undefined && freeBalance !== null
      ? freeBalance.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  const formattedUsedBalance =
    usedBalance !== undefined && usedBalance !== null
      ? usedBalance.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  const SWIPE_THRESHOLD = 60; // Minimum swipe distance to reveal trash icon
  const MAX_SWIPE = 80; // Maximum swipe distance

  // Auto-slide on click (mobile only)
  useEffect(() => {
    if (!isConnected || isDesktop) return;

    if (isClickExpanded && !isSwiping) {
      setSwipeOffset(MAX_SWIPE);
    } else if (!isClickExpanded && !isSwiping) {
      setSwipeOffset(0);
    }
  }, [isClickExpanded, isSwiping, isConnected, isDesktop]);

  const handleTileClick = (e: React.MouseEvent) => {
    if (!isConnected || isDesktop || isSwiping) return;
    // Don't toggle if clicking the trash button or connect button
    if (trashContainerRef.current?.contains(e.target as Node)) return;
    if ((e.target as HTMLElement).closest("button")) return;
    setIsClickExpanded(!isClickExpanded);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isConnected) return;
    startXRef.current = e.touches[0].clientX;
    currentXRef.current = startXRef.current;
    setIsSwiping(true);
    setIsClickExpanded(false); // Disable click-expand when manually swiping
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isConnected || !isSwiping) return;
    currentXRef.current = e.touches[0].clientX;
    const diff = startXRef.current - currentXRef.current;

    // Only allow swiping left (negative diff)
    if (diff > 0) {
      const offset = Math.min(diff, MAX_SWIPE);
      setSwipeOffset(offset);
    } else {
      setSwipeOffset(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isConnected) return;
    setIsSwiping(false);

    // Snap to position based on threshold
    if (swipeOffset >= SWIPE_THRESHOLD) {
      setSwipeOffset(MAX_SWIPE);
      setIsClickExpanded(true); // Keep it expanded
    } else {
      setSwipeOffset(0);
      setIsClickExpanded(false);
    }
  };

  const finishSwipe = () => {
    setIsSwiping(false);
    const currentOffset = swipeOffsetRef.current;
    if (currentOffset >= SWIPE_THRESHOLD) {
      setSwipeOffset(MAX_SWIPE);
      setIsClickExpanded(true); // Keep it expanded
    } else {
      setSwipeOffset(0);
      setIsClickExpanded(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isConnected || isDesktop) return;
    startXRef.current = e.clientX;
    currentXRef.current = startXRef.current;
    setIsSwiping(true);
    setIsClickExpanded(false); // Disable click-expand when manually swiping
  };

  // Reset swipe when clicking outside or mouse up
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isConnected || !isSwiping) return;
      currentXRef.current = e.clientX;
      const diff = startXRef.current - currentXRef.current;

      if (diff > 0) {
        const offset = Math.min(diff, MAX_SWIPE);
        setSwipeOffset(offset);
      } else {
        setSwipeOffset(0);
      }
    };

    const handleMouseUp = () => {
      if (!isConnected) return;
      finishSwipe();
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutsideTile =
        tileRef.current && !tileRef.current.contains(target);
      const isOutsideTrash =
        trashContainerRef.current &&
        !trashContainerRef.current.contains(target);

      if (isOutsideTile && isOutsideTrash) {
        setSwipeOffset(0);
        setIsClickExpanded(false);
      }
    };

    if (isSwiping) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isConnected, isSwiping]);

  const handleTrashClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRemoveDialog(true);
    setSwipeOffset(0);
    setIsClickExpanded(false);
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowRemoveDialog(true);
  };

  const handleRefreshClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRefresh?.();
  };

  const refreshButtonClassName =
    "p-1.5 rounded-md text-text-secondary hover:text-accent hover:bg-accent/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

  const balanceColumns = statusText ? (
    <span className="text-text-secondary text-sm">{statusText}</span>
  ) : isBalancePending ? (
    <div className="flex items-center gap-2 shrink-0 text-text-secondary">
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
      <span className="text-sm whitespace-nowrap">Fetching balance...</span>
    </div>
  ) : (
    <div className="flex gap-4 sm:gap-4 shrink-0">
      <div className="flex flex-col shrink-0">
        <span className="text-text-secondary text-xs uppercase tracking-wide whitespace-nowrap">
          Free
        </span>
        <span className="text-text-primary text-sm font-medium whitespace-nowrap">
          {formattedFreeBalance}{" "}
          <span className="text-text-secondary text-xs">USDT</span>
        </span>
      </div>
      <div className="flex flex-col shrink-0">
        <span className="text-text-secondary text-xs uppercase tracking-wide whitespace-nowrap">
          In Use
        </span>
        <span className="text-text-primary text-sm font-medium whitespace-nowrap">
          {formattedUsedBalance}{" "}
          <span className="text-text-secondary text-xs">USDT</span>
        </span>
      </div>
    </div>
  );

  return (
    <div className="relative overflow-hidden">
      {/* Trash Icon Background (mobile swipe only) */}
      {isConnected && !isDesktop && (
        <div
          ref={trashContainerRef}
          className={`absolute right-0 top-0 h-full w-20 flex items-center justify-center z-0 pointer-events-auto ${!isSwiping ? "transition-opacity duration-200" : ""}`}
          style={{
            opacity: swipeOffset > 0 ? 1 : 0,
            pointerEvents: swipeOffset > 0 ? "auto" : "none",
          }}
        >
          <button
            onClick={handleTrashClick}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="w-12 h-12 rounded-full bg-red-600/50 hover:bg-red-700 flex items-center justify-center transition-colors"
          >
            <Trash2 className="w-5 h-5 text-white" />
          </button>
        </div>
      )}

      {/* Main Tile */}
      <div
        ref={tileRef}
        className={`bg-[#0d121a] rounded-xl p-3 border relative z-[1] border-white/10 hover:border-[#2dcc89]/40 ${isConnected ? (isDesktop ? "cursor-default" : "cursor-grab active:cursor-grabbing") : ""} ${!isSwiping ? "transition-all duration-200" : ""}`}
        style={{
          transform: isDesktop ? undefined : `translateX(-${swipeOffset}px)`,
          touchAction: isConnected && !isDesktop ? "pan-y" : "auto",
        }}
        onClick={handleTileClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={isDesktop ? undefined : handleMouseDown}
      >
        {isConnected && isCompactLayout && (
          <div className="absolute top-2 right-2 z-[2] flex items-center gap-0.5">
            {onRefresh && (
              <button
                onClick={handleRefreshClick}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                disabled={isRefreshing}
                title="Refresh balance"
                className={refreshButtonClassName}
              >
                {isRefreshing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            {isDesktop && (
              <button
                onClick={handleRemoveClick}
                onMouseDown={(e) => e.stopPropagation()}
                title="Remove exchange"
                className="p-1.5 rounded-md text-text-secondary hover:text-red-500 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {isConnected && !isCompactLayout && isDesktop && (
          <button
            onClick={handleRemoveClick}
            onMouseDown={(e) => e.stopPropagation()}
            title="Remove exchange"
            className="absolute top-2 right-2 p-1.5 rounded-md text-text-secondary hover:text-red-500 hover:bg-red-500/10 transition-colors z-[2]"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        {isConnected && onRefresh && !isCompactLayout && (
          <button
            onClick={handleRefreshClick}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            disabled={isRefreshing}
            title="Refresh balance"
            className={`absolute top-2 right-2 z-[2] sm:hidden ${refreshButtonClassName}`}
          >
            {isRefreshing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
          </button>
        )}

        {/* Main Content */}
        <div
          className={
            isCompactLayout
              ? `flex flex-col gap-3 ${isConnected ? (isDesktop ? "pr-16" : "pr-8") : ""}`
              : `flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${isConnected ? (isDesktop ? "sm:pr-8" : "max-sm:pr-8") : ""}`
          }
        >
          {/* Logo and Name */}
          <div
            className={
              isCompactLayout
                ? "flex items-center gap-3 min-w-0"
                : "flex items-center gap-3 min-w-0 sm:w-64 sm:shrink-0"
            }
          >
            {showDragHandle && (
              <button
                type="button"
                aria-label={`Drag to change ${displayName} autotrade priority`}
                className="touch-none shrink-0 p-1 -ml-1 rounded-md text-text-secondary hover:text-text-primary cursor-grab active:cursor-grabbing"
                onPointerDown={(event) => {
                  event.stopPropagation();
                  onDragHandlePointerDown?.(event);
                }}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
              >
                <GripVertical className="w-4 h-4" />
              </button>
            )}
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center overflow-hidden shrink-0">
              <img
                src={exchangeIcons[code as keyof typeof exchangeIcons]}
                alt={displayName}
                className="w-10 h-10 object-cover"
              />
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="text-white text-base font-semibold leading-6 truncate">{displayName}</span>
              {EXCHANGE_SUBTITLES[code] && (
                <span className="text-[#a3a3a3] text-sm leading-5 truncate">{EXCHANGE_SUBTITLES[code]}</span>
              )}
            </div>
          </div>

          {/* Balances — separate row on very small screens or compact layout */}
          {isConnected && (
            <div className={isCompactLayout ? "flex pl-11" : "flex sm:hidden pl-11"}>
              {balanceColumns}
            </div>
          )}

          {/* Balances + refresh — inline from sm and up */}
          {isConnected && !isCompactLayout && (
            <div className="hidden sm:flex items-center gap-3 flex-1 min-w-0">
              <div className="h-8 w-px bg-border shrink-0" />
              {balanceColumns}
              {onRefresh && (
                <button
                  onClick={handleRefreshClick}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  disabled={isRefreshing}
                  title="Refresh balance"
                  className={`ml-1 shrink-0 ${refreshButtonClassName}`}
                >
                  {isRefreshing ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                </button>
              )}
            </div>
          )}

          {/* Connect button */}
          {!isConnected && (
            <div
              className={
                isCompactLayout
                  ? "w-full shrink-0"
                  : "w-full sm:ml-4 sm:w-auto shrink-0 sm:flex sm:items-center sm:gap-6"
              }
            >
              {!isCompactLayout && (
                <span className="hidden sm:flex items-center gap-1.5 text-sm text-[#a3a3a3] whitespace-nowrap">
                  <span className="size-1.5 rounded-full bg-[#a3a3a3]" />
                  Not Connected
                </span>
              )}
              <button
                onClick={onConnect}
                className={`${isCompactLayout ? "w-full" : "w-full sm:w-auto"} h-11 px-4 text-base text-[#2dcc89] font-semibold transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 bg-[#07a66c]/10 border border-[#2dcc89] rounded-xl hover:bg-[#07a66c]/20`}
              >
                <Link2 className="w-5 h-5" aria-hidden />
                Connect
              </button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-500">Failed to fetch balance</p>
            </div>
          </div>
        )}
      </div>

      {/* Remove Confirmation Dialog */}
      <AlertDialog.Root
        open={showRemoveDialog}
        onOpenChange={setShowRemoveDialog}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/80 z-[200] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <AlertDialog.Content className="fixed left-[50%] top-[50%] z-[201] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-secondary border border-border p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
            <AlertDialog.Title className="text-xl font-bold text-text-primary mb-2">
              Remove {displayName}?
            </AlertDialog.Title>
            <AlertDialog.Description className="text-text-secondary mb-6">
              Are you sure you want to remove this exchange connection? This
              action cannot be undone and you'll need to re-enter your API
              credentials to reconnect.
            </AlertDialog.Description>
            <div className="flex gap-3 justify-end">
              <AlertDialog.Cancel asChild>
                <button className="px-4 py-2 rounded-lg border border-border text-text-primary hover:bg-primary transition-colors">
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                  onClick={onRemove}
                >
                  Remove
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
};
